import global from "../global.js";
import Transform from "./Transform.js";
import {DIRECTION_UNKNOWN, AXIS_NORTH, AXIS_SOUTH, AXIS_WEST, AXIS_EAST, DIRECTION_NORTH_WEST, DIRECTION_SOUTH_WEST, AXIS_X, AXIS_Y, AXIS_UNKNOWN} from "./Axis.js";

const log = global.log.getLogger("f_mtac.scene.sub_axis");
log.setLevel(log.levels.WARN);

/**
 * @brief Manage the sub_axis of the square corresponding to the coordinates. Naming of the sub_axis for the X axis : [A,...,J], for Y axis [1,...,10].
 *        SubAxis is instanciated 4 times :
 *              . twice for X axes (top and bottom)
 *              . twice for Y axes (left and right)
 * The mechanism is only present for GIS and geolocalized pictures.
 *   the data is re-created after each transformation (scale or translate) and depends:
 *   . on the zoom level: apply a step if zoom is wide
 *   . on the translation: define coordinates only around the screen
 * @param {*} view
 * @param {*} svg
 * @param {*} conf
 * @param {*} layer
 * @param {*} type, AXIS_X or AXIS_Y
 * @param {*} direction, position of the axis example AXIS_NORTH for north
 * @param {*} axis_coords, position of the square on the axis of the main grid
 */
let SubAxis = function (view, svg, conf, layer, type, direction, coordinates, axis_coords) {
    this.type = AXIS_UNKNOWN;
    this.direction = DIRECTION_UNKNOWN;
    this.is_extended = false;
    let reference_position = conf.get("frame.reference.position");
    let offset_x, offset_y;
    // check for existing ressources
    if (!global.d3.select("#sub_axis #posx" + coordinates.x + "posy" + coordinates.y).size() > 0) {
        this.unique_sub_axis = layer.append("g").attr("id", `posx${coordinates.x}posy${coordinates.y}`);
    } else {
        this.unique_sub_axis = global.d3.select("#sub_axis #posx" + coordinates.x + "posy" + coordinates.y);
    }

    switch (type) {
        case AXIS_X:
            switch (direction) {
                case AXIS_NORTH:
                case AXIS_SOUTH:
                    this.direction = direction;
                    break;
                default:
                    throw `Unknown x axis direction: ${direction}`;
            }
            break;
        case AXIS_Y:
            switch (direction) {
                case AXIS_WEST:
                case AXIS_EAST:
                    this.direction = direction;
                    break;
                default:
                    throw `Unknown y axis direction: ${direction}`;
            }
            break;
        default:
            throw `Unknown axis type: ${type}`;
    }

    this.type = type;
    this.data = null;

    // Creates CSS classes

    this.sub_axis_classes = ["axis"];

    switch (type) {
        case AXIS_X:
            this.sub_axis_classes.push("axis_x");
            break;
        case AXIS_Y:
            this.sub_axis_classes.push("axis_y");
            break;
    }

    switch (direction) {
        case AXIS_NORTH:
            this.sub_axis_classes.push("axis_north");
            break;
        case AXIS_WEST:
            this.sub_axis_classes.push("axis_west");
            break;
        case AXIS_SOUTH:
            this.sub_axis_classes.push("axis_south");
            break;
        case AXIS_EAST:
            this.sub_axis_classes.push("axis_east");
            break;
    }

    this.sub_axis = null;

    /**
     * @brief Init coordinates ranges
     */
    if (this.type === AXIS_X) {
        this.data = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
    } else {
        if (reference_position == DIRECTION_SOUTH_WEST) {
            this.data = ["10", "9", "8", "7", "6", "5", "4", "3", "2", "1"];
        } else if (reference_position == DIRECTION_NORTH_WEST) {
            this.data = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
        }
    }

    this.data = this.data.map((x) => axis_coords[0] + axis_coords[1] + ";" + x);
    log.debug("SubAxis.init_data in data[0]", {data0: this.data});

    /**
     * Draw the axis
     */
    this.draw = () => {
        log.debug("SubAxis.draw IN", {type: type_to_string(), direction: direction_to_string()});

        const sub_axis_selector = "g#sub_axis g." + this.sub_axis_classes.join(".") + "";

        this.sub_axis = this.unique_sub_axis.select(sub_axis_selector);

        define_image_coords();

        if (this.sub_axis.size() === 0) {
            this.sub_axis = this.unique_sub_axis.append("g").attr("class", this.sub_axis_classes.join(" "));
        } else {
            this.sub_axis.html("");
        }

        this.sub_axis_text = this.sub_axis.append("g");
        //log.debug("Axis.draw x_coord_offset", this.sub_axis_text);
        if (this.data != null) {
            // maybe data is not ready yet
            this.sub_axis_text
                .selectAll("text")
                .data(this.data)
                .enter()
                .append("text")
                .text(function (d) {
                    return d;
                });
        }

        this.transform.refresh();
    };

    /**
     * @brief Draw the coordinates coords for an image, with or without geo-location.
     * Create _coord_offset() that will update the drawing depending on k
     */
    const define_image_coords = () => {
        log.debug("SubAxis.define_image_coords IN :");
        // sub_square
        const SW = conf.get("square.width") / 10,
            SH = conf.get("square.height") / 10;

        // D3 position of the start of the sub_axis
        let OX = conf.get("square.width") * coordinates.x + conf.get("origin.x");
        let OY = conf.get("square.width") * coordinates.y + conf.get("origin.y");

        switch (conf.get("frame.reference.position")) {
            case DIRECTION_NORTH_WEST: // standard
                offset_x = OX;
                offset_y = OY;
                break;
            case DIRECTION_SOUTH_WEST: // GIGN
                offset_x = OX;
                offset_y = OY; //     short offset to apply for centering the axis value in the square
                break;
        }

        // axis coords positions (pixel)
        this.x_coord_offset = (k) => (this.type === AXIS_X ? (_, n) => (SW / 2 + offset_x + n * SW) * k : null);
        this.y_coord_offset = (k) => (this.type === AXIS_Y ? (_, m) => (SH / 2 + offset_y + m * SH) * k : null);
    };

    /**
     * @brief Computes sub_axis translation during transformation
     * @param {*} x x translation
     * @param {*} y y translation
     * @param {*} k zoom factor
     * @returns useable SVG parameter with the correct translation
     */
    let axis_translate = (x, y, k) => {
        log.debug("SubAxis.axis_translate IN :");
        var {top, left, bottom, right} = view.margin;
        // H,W correspond to heigh and width of the screen
        var H = svg.height,
            W = svg.width;
        var dx = 0,
            dy = 0,
            square_width = conf.get("square.width"),
            // point at which the sub_axis disapear from screen
            stop_point = square_width * 0.5;
        // x_square and y_square are D3 coordinates
        // the same function is used for all 4 axis
        if (this.type === AXIS_X) {
            dx = x;
            let y_square = square_width * coordinates.y;

            if (this.direction === AXIS_NORTH) {
                // we check if initial position is above in D3 to the top of the screen and if true draw it here.
                // REMEMBER that D3 coordinates origin is at the top left of the screen and the positive x is going to the right of the screen and positive y to the bottom
                // variables are added to simulate a padding
                if (top + 20 < y + (y_square + conf.get("origin.y")) * k) {
                    dy = y + (y_square + conf.get("origin.y")) * k;
                }
                // if top is higher than the original origin we then need to check if we draw it at the top of the screen or just hide it
                else {
                    dy = top + 20 >= y + (y_square + stop_point + conf.get("origin.y")) * k ? y + (y_square + conf.get("origin.y")) * k : top + 20;
                }
            } else if (this.direction === AXIS_SOUTH) {
                if (H - bottom - 20 > y + (y_square + square_width + conf.get("origin.y")) * k) {
                    dy = y + (square_width * (coordinates.y + 1) + conf.get("origin.y")) * k;
                } else {
                    dy = H - bottom - 20 <= y + (y_square + stop_point + conf.get("origin.y")) * k ? y + (square_width * (coordinates.y + 1) + conf.get("origin.y")) * k : H - bottom - 20;
                }
            }
        } else if (this.type === AXIS_Y) {
            dy = y;
            let x_square = square_width * coordinates.x;
            if (this.direction === AXIS_WEST) {
                if (left + 45 < x + (x_square + conf.get("origin.x")) * k + 15) {
                    dx = x + (x_square + conf.get("origin.x")) * k + 15;
                } else {
                    dx = left + 45 >= x + (x_square + stop_point + conf.get("origin.x")) * k ? x + (x_square + conf.get("origin.x")) * k : left + 45;
                }
            } else if (this.direction === AXIS_EAST) {
                if (W - right - 45 > x + (square_width * (coordinates.x + 1) + conf.get("origin.x")) * k - 15) {
                    dx = x + (square_width * (coordinates.x + 1) + conf.get("origin.x")) * k - 15;
                } else {
                    dx = W - right - 45 <= x + (x_square + stop_point + conf.get("origin.x")) * k ? x + (square_width * (coordinates.x + 1) + conf.get("origin.x")) * k : W - left - 45;
                }
            }
        }
        return `translate(${dx},${dy})`;
    };

    this.transform = new Transform({x: 0, y: 0, k: 1});

    /**
     * @brief callback on D3 translation, build the transform atribute
     * @param x,y: translation
     * @param k: zoom factor
     */
    this.transform.on_translate(({x, y, k}) => {
        log.debug("SubAxis.transform.on_translate IN :");
        this.sub_axis_text.attr("transform", `${axis_translate(x, y, k)}`);
    });

    /**
     * @brief callback on D3 zoom
     */
    this.transform.on_scale(({k}) => {
        log.debug(`Axis.transform.on_scale k= ${k}`);
        if (this.x_coord_offset !== undefined && this.y_coord_offset !== undefined) {
            // apply k to the coords
            this.sub_axis_text.selectAll("text").attr("x", this.x_coord_offset(k)).attr("y", this.y_coord_offset(k));
        }
    });

    /**
     * @brief convert int dirtection to string direction
     * @returns string
     */
    const direction_to_string = () => {
        log.debug("SubAxis.direction_to_string IN :");
        switch (this.direction) {
            case AXIS_NORTH:
                return "AXIS_NORTH";
            case AXIS_SOUTH:
                return "AXIS_SOUTH";
            case AXIS_WEST:
                return "AXIS_WEST";
            case AXIS_EAST:
                return "AXIS_EAST";
            default:
                return "unknown";
        }
    };

    /**
     * @brief convert int type to string type for logs
     * @returns string
     */
    const type_to_string = () => {
        log.debug("SubAxis.type_to_string IN :");
        switch (this.type) {
            case AXIS_X:
                return "AXIS_X";
            case AXIS_Y:
                return "AXIS_Y";
            default:
                return "unknown";
        }
    };

    /**
     * @brief used for first drawing of the sub_axis and initializing correctly the tranform and translate parameters, afterward it will updates accordingly with the grid.
     */
    this.init = () => {
        log.debug("SubAxis.init IN :");
        this.draw();
        this.sub_axis_text.attr("transform", `${axis_translate(global.scene.root_transform.x, global.scene.root_transform.y, global.scene.root_transform.k)}`);
        this.sub_axis_text.selectAll("text").attr("x", this.x_coord_offset(global.scene.root_transform.k)).attr("y", this.y_coord_offset(global.scene.root_transform.k));
    };
};

export default SubAxis;
